import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaChevronDown, FaChevronUp, FaUpload, FaFile, FaTrash, FaSave, FaSpinner, FaEye } from "react-icons/fa";

const AdminStep1ChecklistModal = ({ show, onClose, userId, memberIndex, onSaved }) => {
  const MAX_FILES = 20;
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [expanded, setExpanded] = useState({});
  const [files, setFiles] = useState([]);
  const [formQuestions, setFormQuestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [assignedForm, setAssignedForm] = useState('');
  const [userName, setUserName] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);

  const modalRoot = typeof document !== 'undefined' ? document.body : null;

  // Fetch step 1 data and questions when modal opens
  useEffect(() => {
    if (!show || !userId) return;

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem("adminToken");

        // Fetch step 1 checklist data from admin endpoint
        let url = `${process.env.REACT_APP_API_BASE_URL}/api/auth/step1-checklist/${userId}`;
        if (memberIndex !== undefined && memberIndex !== null && memberIndex >= 0) {
          url += `/${memberIndex}`;
        }

        const step1Response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!step1Response.ok) throw new Error('Failed to fetch step 1 data');
        const step1Data = await step1Response.json();

        setAssignedForm(step1Data.assignedForm || '1040');
        setUserName(step1Data.userName || '');
        setIsSubmitted(step1Data.step1Data?.status === 'submitted');
        setSubmittedAt(step1Data.step1Data?.submittedAt || null);

        // Convert Map-like answers/comments to plain objects
        const rawAnswers = step1Data.step1Data?.answers || {};
        const rawComments = step1Data.step1Data?.comments || {};
        const parsedAnswers = rawAnswers instanceof Map ? Object.fromEntries(rawAnswers) : (typeof rawAnswers === 'object' ? rawAnswers : {});
        const parsedComments = rawComments instanceof Map ? Object.fromEntries(rawComments) : (typeof rawComments === 'object' ? rawComments : {});

        setAnswers(parsedAnswers);
        setComments(parsedComments);

        // Convert stored files
        const storedFiles = (step1Data.step1Data?.files || []).map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
          url: file.url,
          isStored: true
        }));
        setFiles(storedFiles);

        // Fetch questions for the form type
        const formType = step1Data.assignedForm || '1040';
        const questionsResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/questions/form/${formType}`);
        if (questionsResponse.ok) {
          const questionsData = await questionsResponse.json();
          setFormQuestions(questionsData.data || {});
        } else {
          setFormQuestions(getFallbackQuestions(formType));
        }
      } catch (err) {
        console.error('Error fetching checklist data:', err);
        setError('Failed to load checklist data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [show, userId, memberIndex]);

  useEffect(() => {
    if (!show || !modalRoot) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = overflow; };
  }, [show, modalRoot]);

  const getFallbackQuestions = (formType) => {
    const fallback = {
      "1040": {
        "Document Upload by Client": [
          "Prior-year tax return copy (for reference)",
          "W-2 forms from all employers",
          "1099-NEC (Self-employment income)",
          "1099-K (Payments from platforms like PayPal, Stripe, etc.)",
          "1099-INT (Interest income)",
          "1099-DIV (Dividends received)",
          "1099-B (Stocks, bonds, or cryptocurrency sales)",
          "Social Security income (SSA-1099)",
          "1099-R (Retirement distributions)",
          "1099-G (Unemployment benefits)",
          "W-2G (Gambling winnings)",
          "1098-E (Student loan interest)",
          "5498 (IRA contributions)",
          "5498-SA (HSA contributions)",
          "1098 (Mortgage interest)",
          "Education credit (1098-T)",
          "Form K1 document If any",
          "Business income & expense records",
          "Rental income & expenses"
        ],
        "Basic Information of Client": [
          "Taxpayer's full name",
          "Taxpayer's SSN",
          "Taxpayer's Date of Birth",
          "Spouse's full name(if applicable)",
          "Spouse's SSN",
          "Spouse's Date of Birth",
          "Current mailing address",
          "Contact details",
          "Filing status (Single, MFJ, MFS, HOH, Qualifying Widow(er))",
          "Dependents' names SSNs, and dates of birth",
          "Dependents SSN",
          "Dependents date of birth",
          "Bank account details for direct deposit:-",
          "Do you want your refund directly deposited?",
          "Do you want your tax balance directly drafted?",
          "Bank Name",
          "Routing Number (9 Digits).",
          "Account Number",
          "Type of Account-Checking or Savings",
          "Quarterly estimated tax payments made",
          "Is you traded in any virtual currency in 2024?"
        ],
        "Are you having any foreign financials accounts (outside USA)?": [
          "FBAR/Form 8938 (Foreign accounts & assets)",
          "Asset Type",
          "Individual/ Joint Ownership",
          "Account Number",
          "Name of Financial Institution/Corporation",
          "Complete Address of Financial Institution/Corporation",
          "Maximum Foreign Currency Value During 2024",
          "Foreign Currency Value as of 12/31/2024",
          "Foreign Currency Unit",
          "Exchange Rate",
          "Form 8621 Mutual Fund or Any Passive Funds Hold outside USA",
          "Maximum Foreign Currency Value During 2024",
          "Foreign Currency Value as of 12/31/2024",
          "Foreign Currency Unit",
          "Exchange Rate",
          "Were you a signatory over any foreign financial accounts?",
          "Did you receive any foreign gifts or inheritances over $100,000?",
          "Do you have any foreign trusts?",
          "Did you have any foreign real estate transactions?",
          "Any other foreign income or assets not listed above?",
          "Comments/Additional Information"
        ]
      }
    };
    return fallback[formType] || fallback["1040"];
  };

  const totalQuestions = Object.values(formQuestions).reduce(
    (total, section) => total + section.length, 0
  );

  const calculateProgress = () => {
    return Object.keys(answers).filter((k) => answers[k] !== undefined && answers[k] !== '').length;
  };

  const calculateSectionProgress = (section) => {
    if (!formQuestions[section]) return 0;
    return formQuestions[section].filter((_, index) => answers[`${section}-${index}`] !== undefined && answers[`${section}-${index}`] !== '').length;
  };

  const formatDateTime = (value) => {
    if (!value) return null;
    try {
      const date = new Date(value);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    } catch (err) {
      return value;
    }
  };

  const handleAnswer = (index, value) => {
    if (!isSubmitted) {
      setAnswers((prev) => ({ ...prev, [index]: value }));
    }
  };

  const handleComment = (index, value) => {
    if (!isSubmitted) {
      setComments((prev) => ({ ...prev, [index]: value }));
    }
  };

  const toggleQuestion = (question) => {
    setExpanded((prev) => ({ ...prev, [question]: !prev[question] }));
  };

  const handleFileChange = (e) => {
    if (!isSubmitted && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const currentFileCount = files.length;
      const availableSlots = MAX_FILES - currentFileCount;

      if (newFiles.length > availableSlots) {
        alert(`You can only upload up to ${MAX_FILES} files. You currently have ${currentFileCount} files.`);
        return;
      }

      const formattedNewFiles = newFiles.map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        progress: 0
      }));

      setFiles(prev => [...prev, ...formattedNewFiles]);
      e.target.value = '';
    }
  };

  const removeFile = (index) => {
    if (!isSubmitted) {
      setFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSave = async () => {
    if (isSubmitted) return;
    setSaving(true);

    try {
      const token = localStorage.getItem("adminToken");

      // Upload new files first if any
      const newFiles = files.filter(f => f.file && !f.isStored);
      let uploadedFileUrls = [];

      if (newFiles.length > 0) {
        const formData = new FormData();
        newFiles.forEach(file => formData.append('files', file.file));

        const uploadResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/upload-files`, {
          method: "POST",
          body: formData,
          headers: { Authorization: `Bearer ${token}` },
        });

        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          uploadedFileUrls = uploadResult.fileUrls || [];
        }
      }

      // Format files for backend
      const formattedFiles = files.map(file => {
        if (file.isStored) {
          return { name: file.name, size: file.size, type: file.type, url: file.url };
        }
        const matchingUrl = Array.isArray(uploadedFileUrls)
          ? uploadedFileUrls.find(url => typeof url === 'string' && url.includes(encodeURIComponent(file.name)))
          : '';
        return { name: file.name, size: file.size, type: file.type, url: matchingUrl || '' };
      });

      // Save via admin endpoint
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/admin-save-step1-checklist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId,
          memberIndex: (memberIndex !== undefined && memberIndex !== null && memberIndex >= 0) ? memberIndex : null,
          answers,
          comments,
          files: formattedFiles
        }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to save checklist');
      }

      const currentProgress = calculateProgress();
      alert(`Saved progress: ${currentProgress}/${totalQuestions} questions answered`);

      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error saving checklist:', err);
      alert(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const getOptionStyles = (option, isSelected) => {
    if (!isSelected) return 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600';
    switch (option) {
      case 'yes': return 'bg-green-500 text-white shadow-lg shadow-green-500/30';
      case 'no': return 'bg-red-500 text-white shadow-lg shadow-red-500/30';
      case 'na': return 'bg-gray-500 text-white shadow-lg shadow-gray-500/30';
      case 'comment': return 'bg-blue-500 text-white shadow-lg shadow-blue-500/30';
      default: return 'bg-gray-100 dark:bg-dark-700';
    }
  };

  if (!show || !modalRoot) return null;

  if (loading) {
    return createPortal(
      <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-2xl">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-blue-200 dark:border-blue-800 border-t-blue-500 rounded-full animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading checklist...</p>
          </div>
        </div>
      </div>,
      modalRoot
    );
  }

  if (error) {
    return createPortal(
      <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-8 shadow-2xl max-w-md">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <FaTimes className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button onClick={onClose} className="px-6 py-2.5 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-all duration-200">Close</button>
          </div>
        </div>
      </div>,
      modalRoot
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                {isSubmitted ? <FaEye className="text-blue-500" /> : null}
                Initial Information (Checklist)
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {userName} {memberIndex >= 0 ? `(Member ${memberIndex + 1})` : '(Primary)'} — Form {assignedForm}
                {isSubmitted && <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">Submitted</span>}
              </p>
              {submittedAt && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Submitted on: <span className="font-semibold text-gray-600 dark:text-gray-300">{formatDateTime(submittedAt)}</span>
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-semibold text-sm">
                {calculateProgress()}/{totalQuestions}
              </span>
              <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-700 text-gray-500 dark:text-gray-400 transition-colors">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>
          {!isSubmitted && (
            <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30">
              <div className="space-y-1 text-sm text-amber-700 dark:text-amber-300">
                <p><span className="font-semibold">Admin Mode:</span> You can fill and save this checklist. Only the user can Confirm & Submit.</p>
              </div>
            </div>
          )}
          {isSubmitted && (
            <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30">
              <div className="space-y-1 text-sm text-green-700 dark:text-green-300">
                <p><span className="font-semibold">Read-Only:</span> This checklist has been submitted by the user. No changes can be made.</p>
              </div>
            </div>
          )}
        </div>

        {/* Questions Container */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {Object.keys(formQuestions).map((questionTitle, index) => {
            const sectionLetter = String.fromCharCode(65 + index);
            const sectionProgress = calculateSectionProgress(questionTitle);
            const isExpanded = expanded[questionTitle];
            return (
              <div key={index} className="rounded-2xl border border-gray-200 dark:border-dark-600 overflow-hidden shadow-sm">
                {/* Section Header */}
                <button
                  onClick={() => toggleQuestion(questionTitle)}
                  className="w-full flex items-center justify-between p-4 bg-white dark:bg-dark-700 hover:bg-gray-50 dark:hover:bg-dark-600 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold text-sm flex items-center justify-center shadow-md shadow-blue-500/30">
                      {sectionLetter}
                    </span>
                    <span className="font-semibold text-gray-900 dark:text-white text-left text-base">{questionTitle}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1.5 rounded-full bg-gray-100 dark:bg-dark-600 text-sm font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-dark-500">
                      {sectionProgress}/{formQuestions[questionTitle].length}
                    </span>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isExpanded ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-dark-600'}`}>
                      {isExpanded ? <FaChevronUp className="w-4 h-4 text-blue-500" /> : <FaChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                  </div>
                </button>

                {/* Section Content */}
                {isExpanded && (
                  <div className="p-4 space-y-3 bg-gray-50 dark:bg-dark-800 border-t border-gray-200 dark:border-dark-600">
                    {formQuestions[questionTitle].map((subQuestion, subIndex) => (
                      <div key={subIndex} className="p-4 rounded-xl bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 shadow-sm">
                        <p className="text-gray-900 dark:text-gray-200 mb-4 leading-relaxed">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-semibold text-xs mr-2">{subIndex + 1}</span>
                          {subQuestion}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {["yes", "no", "na", "comment"].map((option) => (
                            <button
                              key={option}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAnswer(`${questionTitle}-${subIndex}`, option);
                              }}
                              disabled={isSubmitted}
                              className={`px-5 py-2 rounded-lg font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${getOptionStyles(option, answers[`${questionTitle}-${subIndex}`] === option)}`}
                            >
                              {option.toUpperCase()}
                            </button>
                          ))}
                        </div>
                        {answers[`${questionTitle}-${subIndex}`] === "comment" && (
                          <textarea
                            placeholder="Add a comment..."
                            value={comments[`${questionTitle}-${subIndex}`] || ""}
                            onChange={(e) => handleComment(`${questionTitle}-${subIndex}`, e.target.value)}
                            disabled={isSubmitted}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-4 w-full p-4 rounded-xl bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-600 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none"
                            rows={3}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* File Upload Section */}
        {/* <div className="px-6 pb-4">
          <label className={`inline-flex items-center gap-2 py-2 px-4 rounded-full border transition-all duration-200 cursor-pointer text-sm ${isSubmitted ? 'border-gray-300 dark:border-dark-600 bg-gray-100 dark:bg-dark-700/50 cursor-not-allowed' : 'border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 hover:border-blue-500'}`}>
            <input
              type="file"
              onChange={handleFileChange}
              disabled={isSubmitted}
              multiple
              className="hidden"
            />
            <FaUpload className={`w-4 h-4 ${isSubmitted ? 'text-gray-400' : 'text-blue-500'}`} />
            <span className={`font-medium ${isSubmitted ? 'text-gray-400' : 'text-blue-600 dark:text-blue-400'}`}>
              {files.length}/{MAX_FILES} files selected
            </span>
          </label>

          {files.length > 0 && (
            <div className="mt-3 max-h-40 overflow-y-auto space-y-1.5">
              {files.map((file, index) => (
                <div key={index} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FaFile className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-900 dark:text-white truncate" title={file.name}>{file.name}</p>
                      <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  {!isSubmitted && (
                    <button onClick={() => removeFile(index)} className="p-1.5 text-red-500 hover:text-red-600 transition-colors flex-shrink-0" title="Remove file">
                      <FaTrash className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div> */}

        {/* Actions */}
        <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex flex-col sm:flex-row items-center justify-between gap-4">
          <button onClick={onClose} className="w-full sm:w-auto px-6 py-3 rounded-xl font-semibold text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 transition-all duration-200">
            Close
          </button>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {!isSubmitted && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-blue-500/30"
              >
                {saving ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaSave className="w-4 h-4" />}
                {saving ? 'Saving...' : 'Save Progress'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    modalRoot
  );
};

export default AdminStep1ChecklistModal;
